import { Box, Container, styled } from "@mui/material";
import { Sparkles, TrendingUp, Clock, Star, HelpCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

// ===== BENEFITS SECTION =====
const BenefitsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  background: "transparent",
  position: "relative",
}));

const SectionTitle = styled("h2")({
  color: "#ffffff",
  marginBottom: "12px",
  fontSize: "2.5rem",
  textAlign: "center",
  fontWeight: 700,
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "-0.02em",
});

const SectionSubtitle = styled("p")({
  color: "rgba(255, 255, 255, 0.6)",
  fontSize: "1.125rem",
  textAlign: "center",
  marginBottom: "48px",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 400,
  letterSpacing: "0.03em",
});

const BenefitsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(3),
  maxWidth: "1280px",
  margin: "0 auto",
  "@media (min-width: 768px)": {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
}));

const BenefitCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "rgba(255, 255, 255, 0.03)",
  borderRadius: "12px",
  border: "2px solid rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "rgba(255, 107, 53, 0.3)",
    background: "rgba(255, 255, 255, 0.05)",
  }
}));

export function Benefits() {
  const benefits = [
    {
      icon: Sparkles,
      title: "Logic, Not Randomness",
      description: "Most apps just give you random exercises. Our AI uses Progressive Overload logic to ensure you actually get stronger, whether you can do 0 pull-ups or 20."
    },
    {
      icon: TrendingUp,
      title: "Equipment Agnostic",
      description: "Rings, Bar, or Floor? Tell us what gear you have. We don't just \"skip\" exercises; we swap them for biomechanically equivalent variations."
    },
    {
      icon: Clock,
      title: "The \"Anti-App\"",
      description: "No Bloated Subscription. Get a clear, printable PDF. No ads, no paywalls to see your next set, no battery-draining animations. Just the work."
    },
  ];

  return (
    <BenefitsSection>
      <Container maxWidth="xl">
        <SectionTitle>Why WorkoutAIPlan?</SectionTitle>
        <SectionSubtitle>A smarter way to work out</SectionSubtitle>

        <BenefitsGrid>
          {benefits.map((benefit, i) => (
            <BenefitCard key={i}>
              <Box sx={{ width: 48, height: 48, borderRadius: "8px", background: "rgba(255, 107, 53, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                <benefit.icon size={24} color="#FF6B35" />
              </Box>
              <Box sx={{ color: "#ffffff", fontWeight: 600, fontSize: "1.125rem", mb: 1, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em", textTransform: "uppercase" }}>{benefit.title}</Box>
              <Box sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>{benefit.description}</Box>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </Container>
    </BenefitsSection>
  );
}

// ===== SOCIAL PROOF SECTION =====
const SocialProofSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  background: "transparent",
  position: "relative",
}));

const TestimonialCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "rgba(255, 255, 255, 0.03)",
  borderRadius: "12px",
  border: "2px solid rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "rgba(255, 107, 53, 0.3)",
    background: "rgba(255, 255, 255, 0.05)",
  }
}));

export function SocialProof() {
  const testimonials = [
    { name: "Michael C.", role: "", text: "I was stuck at 8 pull-ups for months. The 'Intermediate' logic broke my plateau in 3 weeks. Simple and effective.", rating: 5 },
    { name: "Sarah M.", role: "", text: "Finally a tool that understands I only have a doorframe pull-up bar. No more skipping gym-machine exercises.", rating: 5 },
    { name: "David K.", role: "", text: "The progression from knee pushups to diamond pushups is spot on. Good for beginners.", rating: 5 },
  ];

  return (
    <SocialProofSection>
      <Container maxWidth="xl">
        <SectionTitle>What Our Users Say</SectionTitle>
        <SectionSubtitle>Join 3,200+ people transforming their fitness</SectionSubtitle>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3, maxWidth: "1280px", margin: "0 auto" }}>
          {testimonials.map((t, i) => (
            <TestimonialCard key={i}>
              <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={j} size={16} fill="#FBBF24" color="#FBBF24" />
                ))}
              </Box>
              <Box sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.875rem", mb: 2, lineHeight: 1.6, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>"{t.text}"</Box>
              <Box sx={{ color: "#ffffff", fontSize: "0.875rem", fontWeight: 600, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em", textTransform: "uppercase" }}>{t.name}</Box>
              <Box sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "0.75rem", fontFamily: "'Oswald', sans-serif" }}>{t.role}</Box>
            </TestimonialCard>
          ))}
        </Box>
      </Container>
    </SocialProofSection>
  );
}

// ===== PERSONALIZATION SECTION =====
const PersonalizationSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  background: "transparent",
  position: "relative",
}));

const PersonalizationGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(6),
  alignItems: "center",
  maxWidth: "1280px",
  margin: "0 auto",

  "@media (min-width: 768px)": {
    gridTemplateColumns: "1fr 1fr",
  },
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const TimelineNumber = styled(Box)(({ active }) => ({
  width: "40px",
  height: "40px",
  background: active ? "#22C55E" : "rgba(255, 107, 53, 0.15)",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: active ? "#ffffff" : "#FF6B35",
  fontWeight: 600,
  flexShrink: 0,
  fontFamily: "'Oswald', sans-serif",
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  background: "rgba(255, 107, 53, 0.1)",
  borderRadius: "16px",
  padding: theme.spacing(4),
  border: "2px solid rgba(255, 107, 53, 0.3)",
  backdropFilter: "blur(10px)",
}));

const Badge = styled("span")({
  display: "inline-block",
  padding: "4px 12px",
  background: "#FF6B35",
  color: "#ffffff",
  borderRadius: "999px",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontWeight: 600,
  marginBottom: "12px",
  fontFamily: "'Oswald', sans-serif",
});

const CTAButton = styled("button")(({ theme }) => ({
  marginTop: theme.spacing(4),
  width: "100%",
  padding: theme.spacing(2, 4),
  background: "#FF6B35",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  fontSize: "1rem",
  fontWeight: 700,
  cursor: "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  boxShadow: "0 10px 15px -3px rgba(255, 107, 53, 0.3)",

  "@media (min-width: 640px)": {
    width: "auto",
  },

  "&:hover": {
    background: "#E85A2A",
    transform: "translateY(-2px)",
    boxShadow: "0 20px 25px -5px rgba(255, 107, 53, 0.4)",
  },

  "&:active": {
    transform: "scale(0.98)",
  },
}));

export function Personalization({ onCTAClick }) {
  return (
    <PersonalizationSection>
      <Container maxWidth="xl">
        <PersonalizationGrid>
          {/* Left: Process Timeline */}
          <Box>
            <TimelineItem>
              <TimelineNumber>1</TimelineNumber>
              <Box>
                <Box sx={{ color: "#ffffff", fontWeight: 600, fontSize: "1rem", mb: 0.5, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em", textTransform: "uppercase" }}>Tell us about yourself</Box>
                <Box sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>Quick 5-question quiz about your goals and fitness level</Box>
              </Box>
            </TimelineItem>

            <TimelineItem>
              <TimelineNumber>2</TimelineNumber>
              <Box>
                <Box sx={{ color: "#ffffff", fontWeight: 600, fontSize: "1rem", mb: 0.5, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em", textTransform: "uppercase" }}>Get your customized plan</Box>
                <Box sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>AI generates a unique workout program tailored to you</Box>
              </Box>
            </TimelineItem>

            <TimelineItem>
              <TimelineNumber active>
                <CheckCircle2 size={20} color="#ffffff" />
              </TimelineNumber>
              <Box>
                <Box sx={{ color: "#ffffff", fontWeight: 600, fontSize: "1rem", mb: 0.5, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em", textTransform: "uppercase" }}>Start your transformation</Box>
                <Box sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>Follow your plan and watch it adapt as you progress</Box>
              </Box>
            </TimelineItem>

            <CTAButton onClick={onCTAClick}>
              Start Personalizing Now
              <ArrowRight size={20} />
            </CTAButton>
          </Box>

          {/* Right: Feature Content */}
          <Box>
            <FeatureCard>
              <Badge>Personalized for You</Badge>
              <Box sx={{ color: "#ffffff", fontSize: "1.5rem", fontWeight: 700, mb: 2, fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "-0.02em" }}>No Two Plans Are Alike</Box>

              <Box sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.875rem", lineHeight: 1.6, mb: 2, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>
                Using your quiz answers, WorkoutAIPlan creates a routine just for you – and updates it with AI after each workout.
              </Box>

              <Box sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.875rem", lineHeight: 1.6, mb: 3, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>
                It's like having a personal trainer in your pocket, constantly adapting to help you reach your goals faster.
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircle2 size={20} color="#22C55E" />
                  <Box sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.875rem", fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>Adapts to your progress in real-time</Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircle2 size={20} color="#22C55E" />
                  <Box sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.875rem", fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>Adjusts difficulty based on feedback</Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircle2 size={20} color="#22C55E" />
                  <Box sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.875rem", fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>Works with your schedule and equipment</Box>
                </Box>
              </Box>
            </FeatureCard>
          </Box>
        </PersonalizationGrid>
      </Container>
    </PersonalizationSection>
  );
}

// ===== FAQ SECTION =====
const FAQSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  background: "transparent",
  position: "relative",
}));

export function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { q: "What do I get for $9.99?", a: "A complete 4-week progressive calisthenics program, tracking sheets, warm-up/cool-down routines, and nutrition guide. One-time payment, lifetime access." },
    { q: "How does the AI work?", a: "Our AI analyzes your fitness level, goals, and available equipment to create a custom workout plan with progressive overload." },
    { q: "Can I use this at home?", a: "Absolutely! We support home workouts with minimal or no equipment, as well as gym-based plans." },
    { q: "What if I'm a complete beginner?", a: "Perfect! Our plans start from absolute beginner level and progress at your pace." },
  ];

  return (
    <FAQSection>
      <Container maxWidth="md">
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        <SectionSubtitle>Everything you need to know</SectionSubtitle>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {faqs.map((faq, i) => (
            <Box key={i} sx={{ background: "rgba(255, 255, 255, 0.03)", borderRadius: "12px", border: "2px solid rgba(255, 255, 255, 0.1)", overflow: "hidden", backdropFilter: "blur(10px)", "&:hover": { borderColor: "rgba(255, 107, 53, 0.3)" } }}>
              <Box
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                sx={{ padding: 3, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <Box sx={{ color: "#ffffff", fontWeight: 600, fontSize: "1rem", fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em", textTransform: "uppercase" }}>{faq.q}</Box>
                <HelpCircle size={20} color="#FF6B35" />
              </Box>
              {openIndex === i && (
                <Box sx={{ px: 3, pb: 3, color: "rgba(255, 255, 255, 0.7)", fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>{faq.a}</Box>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </FAQSection>
  );
}

// ===== FINAL CTA SECTION =====
const FinalCTASection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 2),
  background: "rgba(255, 107, 53, 0.1)",
  borderTop: "2px solid rgba(255, 107, 53, 0.3)",
  textAlign: "center",
  position: "relative",
}));

const FinalCTAButton = styled("button")(({ theme }) => ({
  padding: theme.spacing(2, 6),
  background: "#FF6B35",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  fontSize: "1.125rem",
  fontWeight: 700,
  cursor: "pointer",
  transition: "all 0.3s ease",
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  boxShadow: "0 10px 15px -3px rgba(255, 107, 53, 0.3)",

  "&:hover": {
    transform: "translateY(-2px)",
    background: "#E85A2A",
    boxShadow: "0 20px 25px -5px rgba(255, 107, 53, 0.4)",
  },

  "&:active": {
    transform: "scale(0.98)",
  },
}));

export function FinalCTA({ onCTAClick }) {
  return (
    <FinalCTASection>
      <Container maxWidth="md">
        <Box sx={{ color: "#ffffff", fontSize: "2.25rem", fontWeight: 700, mb: 2, fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "-0.02em" }}>
          Ready to Start Your Transformation?
        </Box>
        <Box sx={{ color: "rgba(255,255,255,0.8)", fontSize: "1.125rem", mb: 4, fontFamily: "'Oswald', sans-serif", fontWeight: 400, letterSpacing: "0.03em" }}>
          Get your personalized 4-week program in seconds
        </Box>
        <FinalCTAButton onClick={onCTAClick}>Get My Plan — $9.99</FinalCTAButton>
      </Container>
    </FinalCTASection>
  );
}

// ===== STICKY MOBILE CTA =====
const StickyButton = styled("button")(({ theme, visible }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2, 4),
  background: "#FF6B35",
  color: "#ffffff",
  border: "none",
  fontSize: "1rem",
  fontWeight: 700,
  cursor: "pointer",
  zIndex: 1000,
  boxShadow: "0 -4px 6px -1px rgba(255, 107, 53, 0.3)",
  transform: visible ? "translateY(0)" : "translateY(100%)",
  transition: "transform 0.3s ease",
  display: "block",
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  "@media (min-width: 768px)": {
    display: "none",
  },
}));

export function StickyMobileCTA({ onCTAClick }) {
  const [visible, setVisible] = useState(false);

  // Show sticky CTA after scrolling down
  useState(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <StickyButton visible={visible} onClick={onCTAClick}>
      Get My Plan — $9.99 ⚡
    </StickyButton>
  );
}

// ===== FOOTER =====
const FooterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  background: "#000000",
  borderTop: "1px solid rgba(255, 107, 53, 0.2)",
}));

export function LandingFooter() {
  return (
    <FooterContainer>
      <Container maxWidth="xl">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 4, mb: 4 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box
                component="img"
                src="/workoutaiplan.png"
                alt="WorkoutAIPlan Logo"
                sx={{ width: 24, height: 24 }}
              />
              <Box sx={{ color: "#ffffff", fontWeight: 700, fontSize: "1.125rem", fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em", textTransform: "uppercase" }}>WorkoutAIPlan</Box>
            </Box>
            <Box sx={{ color: "#9CA3AF", fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "'Oswald', sans-serif", fontWeight: 400 }}>
              AI-powered calisthenics workout plans. Science-based progressions delivered instantly.
            </Box>
          </Box>

          <Box>
            <Box sx={{ color: "#ffffff", fontWeight: 600, fontSize: "0.875rem", mb: 2, textTransform: "uppercase", fontFamily: "'Oswald', sans-serif", letterSpacing: "0.05em" }}>Navigation</Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Link to="/" style={{ color: "#9CA3AF", fontSize: "0.875rem", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#FF6B35"} onMouseLeave={(e) => e.target.style.color = "#9CA3AF"}>Home</Link>
              <Link to="/blog" style={{ color: "#9CA3AF", fontSize: "0.875rem", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#FF6B35"} onMouseLeave={(e) => e.target.style.color = "#9CA3AF"}>Blog</Link>
              <Link to="/about" style={{ color: "#9CA3AF", fontSize: "0.875rem", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#FF6B35"} onMouseLeave={(e) => e.target.style.color = "#9CA3AF"}>About</Link>
              <Link to="/privacy" style={{ color: "#9CA3AF", fontSize: "0.875rem", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#FF6B35"} onMouseLeave={(e) => e.target.style.color = "#9CA3AF"}>Privacy</Link>
            </Box>
          </Box>

          <Box>
            <Box sx={{ color: "#ffffff", fontWeight: 600, fontSize: "0.875rem", mb: 2, textTransform: "uppercase", fontFamily: "'Oswald', sans-serif", letterSpacing: "0.05em" }}>Popular Articles</Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Link to="/blog/ai-workout-plan-for-beginners" style={{ color: "#9CA3AF", fontSize: "0.875rem", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#FF6B35"} onMouseLeave={(e) => e.target.style.color = "#9CA3AF"}>AI Workout for Beginners</Link>
              <Link to="/blog/best-ai-fitness-apps-2025" style={{ color: "#9CA3AF", fontSize: "0.875rem", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#FF6B35"} onMouseLeave={(e) => e.target.style.color = "#9CA3AF"}>Best AI Fitness Apps</Link>
              <Link to="/blog/calisthenics-workout-plan-complete-guide" style={{ color: "#9CA3AF", fontSize: "0.875rem", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#FF6B35"} onMouseLeave={(e) => e.target.style.color = "#9CA3AF"}>Calisthenics Guide</Link>
            </Box>
          </Box>
        </Box>

        <Box sx={{ pt: 4, borderTop: "1px solid #1F2937", textAlign: "center", color: "#6B7280", fontSize: "0.75rem" }}>
          © {new Date().getFullYear()} WorkoutAIPlan. All rights reserved.
        </Box>
      </Container>
    </FooterContainer>
  );
}
